<template>
  <section ref="page" class="c-search-invites">
    <div v-if="withHeader" class="header">
      <h4 class="title" :class="{ 'has-text-white': darkMode }">
        {{ pageTitle }}
      </h4>
      <p
        v-if="pageDescription"
        class="description"
        :class="{ 'has-text-white': darkMode }"
      >
        {{ pageDescription }}
      </p>
    </div>

    <div class="box">
      <transition name="fade-in">
        <InvitesNotFound
          v-if="notFound"
          :showAllInvites="
            !!rsvpConfigs?.preferences?.closed?.show_all_invites || false
          "
          :thereArentInvites="thereArentInvites"
          @showAll="getAllInvites"
        />

        <InvitesResults
          v-if="!notFound && invites.length > 0 && !inviteSelected"
          @resetSearch="resetSearch"
          @selected="onInviteSelected"
          :invites="invites"
        />

        <InviteConfirm4Digits
          v-if="inviteSelected && !inviteConfirmed"
          @resetSearch="resetSearch"
          @confirmed="onInviteConfirmed"
          :invite="inviteSelected"
        />

        <InviteConfirmation
          v-if="inviteConfirmed && rsvpConfigs"
          :invite="inviteConfirmed"
          :configs="rsvpConfigs"
        />
      </transition>

      <form
        v-if="invites.length <= 0 && !thereArentInvites"
        @submit.prevent="searchByName"
      >
        <div class="field">
          <BaseInputOutlined
            v-model="search"
            class="has-text-left"
            label="<strong>*Nome do convite:</strong> (Ex.: Tia Ana  e Família)"
          />
        </div>

        <button
          :disabled="!search || search?.length < 3 || loading"
          :class="{ 'is-loading': loading }"
          type="submit"
          class="button is-primary"
        >
          Pesquisar
        </button>
      </form>
    </div>
  </section>
</template>

<script>
import BaseInputOutlined from '@/components/BaseInputOutlined.vue'
import InvitesResults from './InvitesResults.vue'
import InvitesNotFound from './InvitesNotFound.vue'
import InviteConfirm4Digits from './InviteConfirm4Digits.vue'
import InviteConfirmation from './InviteConfirmation.vue'
import RsvpApi from '@/lib/api/Rsvp'

export default {
  components: {
    BaseInputOutlined,
    InvitesResults,
    InvitesNotFound,
    InviteConfirm4Digits,
    InviteConfirmation,
  },

  computed: {
    api() {
      const noToken = undefined
      const { wed_id } = this.$route.query

      return new RsvpApi(noToken, wed_id || 5)
    },

    pageTitle() {
      return this.rsvpConfigs?.page_title || 'Confirme sua presença'
    },

    pageDescription() {
      return this.rsvpConfigs?.page_introduction || false
    },
  },

  data() {
    return {
      search: '',
      loading: false,
      notFound: false,
      thereArentInvites: false,
      invites: [],
      rsvpConfigs: undefined,
      inviteSelected: undefined,
      inviteConfirmed: undefined,
      darkMode: false,
      withHeader: false,
    }
  },

  mounted() {
    const { dark_mode, with_header } = this.$route.query
    this.darkMode = !!dark_mode
    this.withHeader = !!with_header
    this.getRsvpConfigs()

    this.sendPageHeightPostMessage()
    this.observePageHeight()
  },

  methods: {
    searchByName() {
      if (this.loading) return
      this.loading = true

      const normalizedSearch = this.search
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

      this.api
        .searchInvite(normalizedSearch)
        .then((response) => {
          this.notFound = false
          this.invites = response.data
        })
        .catch((error) => {
          this.notFound = true
        })
        .finally(() => {
          setTimeout(() => {
            this.loading = false
          }, 1000)
        })
    },

    getAllInvites() {
      if (this.loading) return
      this.loading = true

      this.api
        .getAllInvites()
        .then((response) => {
          if (response.data.error) throw response.data
          this.notFound = false
          this.thereArentInvites = false
          this.invites = response.data
        })
        .catch(() => {
          this.notFound = true
          this.thereArentInvites = true
        })
        .finally(() => {
          setTimeout(() => {
            this.loading = false
          }, 1000)
        })
    },

    resetSearch() {
      this.invites = []
      this.notFound = false
      this.search = ''
      this.inviteSelected = undefined
      this.inviteConfirmed = undefined
    },

    onInviteSelected(invite) {
      this.inviteSelected = invite
    },

    onInviteConfirmed(invite) {
      this.inviteConfirmed = invite
    },

    getRsvpConfigs() {
      this.api.getConfigsRsvp().then((response) => {
        this.rsvpConfigs = response?.data?.data
      })
    },

    sendPageHeightPostMessage() {
      const target = this.$refs.page
      window.parent.postMessage(
        {
          event: 'page-height',
          value: target.offsetHeight,
        },
        '*'
      )
    },

    observePageHeight() {
      const target = this.$refs.page

      const childListObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'subtree') {
            this.sendPageHeightPostMessage()
          }
        })
      })

      childListObserver.observe(target, {
        childList: true,
        subtree: true,
      })

      const resizeObserver = new ResizeObserver(() => {
        this.sendPageHeightPostMessage()
      })

      resizeObserver.observe(target)
    },
  },
}
</script>

<style lang="scss">
.c-search-invites {
  max-width: 649px;
  width: calc(100% - 32px);
  margin: 0 auto;
  text-align: center;

  .header {
    margin-bottom: 40px;
  }

  .title {
    margin-bottom: 16px;
    color: $neutral;
    font-size: 32px;
    font-weight: 700;
    line-height: normal;
  }

  .description {
    color: $neutral;
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
  }

  .link {
    display: inline-flex;
    align-items: center;
    font-size: 16px;
    color: $tertiary;
    font-weight: 700;
    line-height: 1;
    border: 0;
    cursor: pointer;
    background-color: transparent;
    &:focus,
    &:hover {
      outline: none;
      color: $tertiarydarkest;
    }
    .svg-icon {
      margin-left: 8px;
    }
    &.icon-left .svg-icon {
      margin-left: 0;
      margin-right: 8px;
    }
  }

  .box {
    padding: 40px 16px;
    border-radius: 16px;
    border: 1px solid #d9e2eb;
    text-align: center;
    background-color: $white;
    box-shadow: 0px 10px 15px 0px rgba(93, 117, 141, 0.1);
    @media screen and (min-width: 480px) {
      padding: 40px;
    }

    .field {
      margin-bottom: 16px;
    }
  }

  .count-tag {
    display: inline-flex;
    padding: 8px 16px;
    margin-bottom: 32px;
    justify-content: center;
    align-items: center;
    font-size: 18px;
    font-weight: 700;
    color: $neutral;
    border-radius: 8px;
    background-color: $neutrallightest;
    @media screen and (min-width: $mobile) {
      margin-bottom: 40px;
    }
  }
}
</style>
